// The colour a taster judged a wine to be (colour, sparkling, tint), kept on this device by wine id
// so the next tasting of that bottle starts from it. It sits under a proef. key like the run, so
// "delete my data" clears it (lib/localData.ts); it never leaves the device and is not part of any
// request. The newest wines win once the cap is reached.
import { HUES_BY_COLOUR, WINE_COLOUR, type Hue, type WineColour } from "@proef/domain";
import { z } from "zod";
import { emptyDraft, type NoteDraft } from "../noteDraft";

const KEY = "proef.wineLook";
const CAP = 200;

export interface WineLook {
  readonly colour: WineColour;
  readonly sparkling: boolean;
  readonly hue: Hue;
}

const Look = z.object({
  colour: z.enum(WINE_COLOUR),
  sparkling: z.boolean(),
  hue: z.string(),
});
const Store = z.record(z.string(), Look);

function belongs(look: z.infer<typeof Look>): look is WineLook {
  return (HUES_BY_COLOUR[look.colour] as readonly string[]).includes(look.hue);
}

function read(): Record<string, z.infer<typeof Look>> {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return {};
    const parsed = Store.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}

export function recallLook(wineId: string): WineLook | undefined {
  const look = read()[wineId];
  return look !== undefined && belongs(look) ? look : undefined;
}

export function rememberLook(wineId: string, look: WineLook): void {
  // Re-inserting moves the wine to the end of the key order, which is how "newest" is told.
  const { [wineId]: _previous, ...others } = read();
  const kept = Object.entries(others).slice(-(CAP - 1));
  try {
    window.localStorage.setItem(KEY, JSON.stringify(Object.fromEntries([...kept, [wineId, look]])));
  } catch {
    // Storage blocked (private mode): the look is simply not remembered.
  }
}

/**
 * The draft with the remembered colour, sparkling and tint pre-selected. What depends on them (the
 * tannin of a red, the mousse of a sparkling wine) follows, unless that part did not change.
 */
export function withLook(draft: NoteDraft, look: WineLook | undefined): NoteDraft {
  if (look === undefined) return draft;
  const fresh = emptyDraft(draft.depth, look.colour, look.sparkling);
  return {
    ...draft,
    colour: look.colour,
    sparkling: look.sparkling,
    hue: look.hue,
    tannin: look.colour === draft.colour ? draft.tannin : fresh.tannin,
    mousse: look.sparkling === draft.sparkling ? draft.mousse : fresh.mousse,
  };
}
